import { getSessionConfig } from '../../supabase/functions/generate-meditation/session-length-utils';

export interface SessionLengthConfig {
  targetWordCount: number;
  maxTokens: number;
  description: string;
}

export const SESSION_LENGTH_MAP: Record<string, SessionLengthConfig> = {
  '15 seconds': {
    targetWordCount: 30,   // Perfect for social media hooks
    maxTokens: 1000,       // Small token limit for quick generation
    description: '15-second social media teaser (25-35 words)'
  },
  '30 seconds': {
    targetWordCount: 60,   // Quick value delivery
    maxTokens: 1500,       // Optimized for short content
    description: '30-second quick session (50-70 words)'
  },
  '45 seconds': {
    targetWordCount: 90,   // Extended social content
    maxTokens: 2000,       // Room for complete thoughts
    description: '45-second focused moment (80-100 words)'
  },
  '1 minute': {
    targetWordCount: 120,  // Full minute experience
    maxTokens: 2500,       // Complete mini-session
    description: '1-minute mindful break (100-140 words)'
  },
  '3 minutes': {
    targetWordCount: 450,  // Safe under ElevenLabs memory limit
    maxTokens: 6000,       // Match working functions - room for complete therapeutic endings
    description: '3-minute focused session (400-500 words)'
  },
  '5 minutes': {
    targetWordCount: 750,  // Safe under ElevenLabs memory limit
    maxTokens: 6000,       // Match working functions - room for complete therapeutic endings
    description: '5-minute therapeutic session (700-800 words)'
  },
  '7 minutes': {
    targetWordCount: 1000, // Safe under ElevenLabs memory limit
    maxTokens: 6000,       // Match working functions - room for complete therapeutic endings
    description: '7-minute deep session (950-1050 words)'
  }
};

export function getSessionConfigForFrontend(sessionLength: string | number): SessionLengthConfig {
  // Handle numeric seconds format (e.g., 300, 180, etc.)
  if (typeof sessionLength === 'number' || !isNaN(Number(sessionLength))) {
    const seconds = Number(sessionLength);

    // Map seconds to closest session length
    if (seconds <= 20) return SESSION_LENGTH_MAP['15 seconds'];
    if (seconds <= 35) return SESSION_LENGTH_MAP['30 seconds'];
    if (seconds <= 52) return SESSION_LENGTH_MAP['45 seconds'];
    if (seconds <= 90) return SESSION_LENGTH_MAP['1 minute'];
    if (seconds <= 240) return SESSION_LENGTH_MAP['3 minutes'];
    if (seconds <= 360) return SESSION_LENGTH_MAP['5 minutes'];
    return SESSION_LENGTH_MAP['7 minutes'];
  }

  // Extract session length from string (handles formats like "5 minutes", "30 seconds", etc.)
  const normalizedLength = String(sessionLength).toLowerCase();

  // Check for seconds first
  if (normalizedLength.includes('15') && normalizedLength.includes('second')) {
    return SESSION_LENGTH_MAP['15 seconds'];
  }
  if (normalizedLength.includes('30') && normalizedLength.includes('second')) {
    return SESSION_LENGTH_MAP['30 seconds'];
  }
  if (normalizedLength.includes('45') && normalizedLength.includes('second')) {
    return SESSION_LENGTH_MAP['45 seconds'];
  }

  // Check for minutes
  if (normalizedLength.includes('1') && normalizedLength.includes('minute') && !normalizedLength.includes('3') && !normalizedLength.includes('5') && !normalizedLength.includes('7')) {
    return SESSION_LENGTH_MAP['1 minute'];
  }
  if (normalizedLength.includes('3')) {
    return SESSION_LENGTH_MAP['3 minutes'];
  }
  if (normalizedLength.includes('5')) {
    return SESSION_LENGTH_MAP['5 minutes'];
  }
  if (normalizedLength.includes('7')) {
    return SESSION_LENGTH_MAP['7 minutes'];
  }

  // Default to 5 minutes if no match
  console.warn(`⚠️ Unknown session length: ${sessionLength}, defaulting to 5 minutes`);
  return SESSION_LENGTH_MAP['5 minutes'];
}

/**
 * Replaces dynamic placeholders in prompt templates with actual session values
 * @param promptTemplate - Template with {{SESSION_LENGTH}} and {{TARGET_WORDS}} placeholders
 * @param sessionLength - User-selected session length (e.g., "5 minutes" or 300)
 * @returns Processed prompt with actual values
 */
export function processSessionLengthPlaceholders(promptTemplate: string, sessionLength: string | number): string {
  const config = getSessionConfigForFrontend(sessionLength);

  // Convert numeric seconds to human-readable format
  let humanReadableLength: string;
  if (typeof sessionLength === 'number' || !isNaN(Number(sessionLength))) {
    const seconds = Number(sessionLength);
    const minutes = Math.round(seconds / 60);
    if (seconds < 60) {
      humanReadableLength = `${seconds} seconds`;
    } else {
      humanReadableLength = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  } else {
    humanReadableLength = String(sessionLength);
  }

  return promptTemplate
    .replace(/\{\{SESSION_LENGTH\}\}/g, humanReadableLength)
    .replace(/\{\{TARGET_WORDS\}\}/g, `~${config.targetWordCount}`);
}
